import {
	OrderQuoteVetterLinkStatus,
	useRevokeOrderQuoteVetterLinkMutation,
	type GetOrderQuoteVetterJobsQuery,
} from '@codezo/graphql';
import { Copy, Link2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { formatDate } from '../../../lib/utils';
import { Button } from '../../ui/button';

type VetterLink = GetOrderQuoteVetterJobsQuery['getOrderQuoteVetterJobs'][number]['links'][number];

/**
 * The people reached by secure link, nested under their supplier. Revoking one
 * ends only that person's access — the request stays, for the supplier's
 * accounts and anyone else's link.
 */
export function VetterJobLinks({
	links,
	editable,
	onChanged,
}: {
	links: VetterLink[];
	editable: boolean;
	onChanged: () => void;
}) {
	const [confirmId, setConfirmId] = useState<string | null>(null);
	const [revoke, { loading: revoking }] = useRevokeOrderQuoteVetterLinkMutation();

	if (links.length === 0) return null;

	const handleCopy = async (link: string) => {
		try {
			await navigator.clipboard.writeText(link);
			toast.success('Link copied');
		} catch {
			toast.error('Could not copy the link');
		}
	};

	const handleRevoke = async (id: string) => {
		try {
			const { data } = await revoke({ variables: { id } });
			const res = data?.revokeOrderQuoteVetterLink;
			if (!res?.success) throw new Error(res?.message || 'Could not revoke the link');
			toast.success('Link revoked');
			setConfirmId(null);
			onChanged();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Could not revoke the link');
		}
	};

	return (
		<div className="flex flex-col gap-1">
			<div className="text-xs font-medium text-text-2">Secure links</div>
			{links.map((l) => {
				const active = l.status === OrderQuoteVetterLinkStatus.Active;
				return (
					<div
						key={l.id}
						className="rounded-[var(--r-2)] bg-surface-sunken px-2.5 py-1.5"
					>
						<div className="flex items-center gap-2">
							<Link2 size={12} className="shrink-0 text-text-4" />
							<span
								className={
									active
										? 'min-w-0 flex-1 truncate font-mono text-xs text-text-1'
										: 'min-w-0 flex-1 truncate font-mono text-xs text-text-4 line-through'
								}
							>
								{l.email}
							</span>
							<span className="shrink-0 text-2xs text-text-4">
								{l.revokedAt
									? `Revoked ${formatDate(l.revokedAt)}`
									: l.createdAt
										? `Sent ${formatDate(l.createdAt)}`
										: ''}
							</span>
							{active && editable && confirmId !== l.id && (
								<>
									<Button
										variant="ghost"
										size="xs"
										onClick={() => handleCopy(l.link)}
									>
										<Copy size={11} data-icon="inline-start" />
										Copy
									</Button>
									<Button
										variant="ghost"
										size="xs"
										onClick={() => setConfirmId(l.id)}
									>
										Revoke
									</Button>
								</>
							)}
						</div>
						{confirmId === l.id && (
							<div className="mt-1.5 flex flex-wrap items-center gap-2 border-t border-line-1 pt-1.5">
								<span className="flex-1 text-xs text-text-3">
									Revoke {l.email}&apos;s link? The request itself stays.
								</span>
								<Button
									variant="outline"
									size="xs"
									onClick={() => setConfirmId(null)}
									disabled={revoking}
								>
									Cancel
								</Button>
								<Button
									variant="destructive"
									size="xs"
									onClick={() => handleRevoke(l.id)}
									disabled={revoking}
								>
									{revoking ? 'Revoking…' : 'Revoke'}
								</Button>
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
}
